import { clsx } from 'clsx';
import { STRATEGY_DEFINITIONS } from '../../types';
import type { JobAttribution } from '../../types';

interface StrategyBadgeProps {
  attribution: JobAttribution;
  size?: 'sm' | 'md' | 'lg';
  showTooltip?: boolean;
  className?: string;
}

export function StrategyBadge({ 
  attribution, 
  size = 'sm', 
  showTooltip = true,
  className 
}: StrategyBadgeProps) {
  const { strategy_key, confidence_score } = attribution;
  
  if (!strategy_key) {
    return null;
  }

  const strategy = STRATEGY_DEFINITIONS[strategy_key];
  
  if (!strategy) {
    // Fallback for unknown strategies
    return (
      <span
        className={clsx(
          "inline-flex items-center rounded-full bg-gray-500/20 text-gray-400",
          {
            'px-2 py-0.5 text-xs': size === 'sm',
            'px-2.5 py-1 text-sm': size === 'md', 
            'px-3 py-1.5 text-base': size === 'lg',
          },
          className
        )}
      >
        {strategy_key.replace(/_/g, ' ')}
      </span>
    );
  }

  const confidenceColor = confidence_score 
    ? confidence_score > 0.8 
      ? 'ring-green-500/30' 
      : confidence_score > 0.6 
        ? 'ring-yellow-500/30' 
        : 'ring-gray-500/30'
    : '';

  return (
    <span
      className={clsx(
        "inline-flex items-center rounded-full font-medium ring-1 ring-inset",
        strategy.color.replace('bg-', 'bg-').replace('-500', '-500/20'),
        strategy.color.replace('bg-', 'text-').replace('-500', '-300'),
        strategy.color.replace('bg-', 'ring-').replace('-500', '-500/30'),
        confidenceColor && `ring-2 ${confidenceColor}`,
        {
          'px-2 py-0.5 text-xs': size === 'sm',
          'px-2.5 py-1 text-sm': size === 'md',
          'px-3 py-1.5 text-base': size === 'lg',
        },
        className
      )}
      title={showTooltip ? `${strategy.description}${confidence_score ? ` (${Math.round(confidence_score * 100)}% confidence)` : ''}` : undefined}
    >
      {strategy.name}
      {confidence_score && size !== 'sm' && (
        <span className="ml-1 text-xs opacity-75">
          {Math.round(confidence_score * 100)}%
        </span>
      )}
    </span>
  );
}